import { Controller } from "@hotwired/stimulus"

// About page choreography (pages/about). Styles live in application.css under
// "About page". Disney principles, applied to the coin, mascots and numbers only:
// the photo itself is never squashed.
//
// - Portrait: a gold coin crouches (anticipation), flips through the air on an
//   arc (arcs, stretch), widens into the frame and squashes on landing, settles
//   (slow in / slow out), then irises open onto the photo, whose zoom lags a
//   beat behind (follow-through).
// - Mascots: dip, stretch up, squash on landing, settle, then idle bob.
// - Numbers: count up with ease-out, then land with a small squash.
// - Mission quote: words light up as you scroll through it.
//
// Hidden start states only apply under .about-js, so no-JS and reduced-motion
// visitors get the finished page.
const COIN_FLIGHT_MS = 1250
const COIN_LAND_AT = 0.84
const START_DELAY_MS = 250

export default class extends Controller {
  static targets = ["portrait", "portraitBody", "photo", "coin", "section", "mascot", "number", "quote", "cue"]

  connect() {
    this.splitQuote()
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (this.reduced) {
      this.lightWords(1)
      return
    }

    this.clearPlayedState()
    this.element.classList.add("about-js")
    this.numberTargets.forEach((el) => { el.textContent = "0" })
    this.revealPortrait()
    this.observeSections()

    this.onScroll = () => this.lightQuote()
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.lightQuote()

    // Turbo snapshots the DOM as it stands; drop the start states so a restored
    // preview shows the finished page instead of hidden pieces.
    this.beforeCache = () => this.settle()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    this.observer?.disconnect()
    clearTimeout(this.landTimer)
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll)
    if (this.beforeCache) document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  // A Turbo-restored snapshot carries the classes and inline styles of the last
  // run (is-landed shows the ring track before the coin has flown). Wipe them
  // so every visit starts from the same place.
  clearPlayedState() {
    this.portraitTarget.classList.remove("is-landed")
    this.portraitTarget.style.perspective = ""
    this.portraitBodyTarget.style.opacity = ""
    this.portraitBodyTarget.style.transform = ""
    this.element.querySelector(".about-hero")?.classList.remove("is-in")
    this.sectionTargets.forEach((el) => el.classList.remove("is-in"))
    this.cueTargets.forEach((el) => el.classList.remove("is-in"))
    this.mascotTargets.forEach((el) => el.classList.remove("is-idle"))
    ;[this.portraitBodyTarget, this.coinTarget, this.photoTarget].forEach((el) => el.getAnimations().forEach((a) => a.cancel()))
  }

  settle() {
    this.clearPlayedState()
    this.element.classList.remove("about-js")
    this.numberTargets.forEach((el) => {
      const host = el.closest("[data-count]")
      if (host) el.textContent = host.dataset.count
    })
    this.lightWords(1)
  }

  // ── Portrait ──────────────────────────────────────────────────────────
  revealPortrait() {
    const body = this.portraitBodyTarget
    const T = (x, y, sx, sy, ry, r) => `translate(${x}px, ${y}px) scale(${sx}, ${sy}) rotateY(${ry}deg) rotate(${r}deg)`

    body.animate([
      { opacity: 0, transform: T(0, 70, 0.3, 0.3, 0, 0) },
      { opacity: 1, transform: T(0, 70, 0.34, 0.34, 0, 0), offset: 0.12 },
      { opacity: 1, transform: T(0, 80, 0.42, 0.26, 0, 0), offset: 0.26, easing: "cubic-bezier(.2,.8,.3,1)" },
      { opacity: 1, transform: T(-12, -34, 0.5, 0.6, 540, -6), offset: 0.55 },
      { opacity: 1, transform: T(0, -10, 0.62, 0.62, 720, 0), offset: 0.72, easing: "cubic-bezier(.6,0,.9,.5)" },
      { opacity: 1, transform: T(0, 6, 1.1, 0.9, 720, 0), offset: COIN_LAND_AT, easing: "cubic-bezier(.2,.8,.3,1)" },
      { opacity: 1, transform: T(0, -4, 0.98, 1.03, 720, 0), offset: 0.93 },
      { opacity: 1, transform: T(0, 0, 1, 1, 720, 0) }
    ], { duration: COIN_FLIGHT_MS, delay: START_DELAY_MS, fill: "both" }).finished.then((anim) => {
      // Drop the 3D flip once landed so the reveal isn't repainting a 3D layer.
      body.style.opacity = "1"
      body.style.transform = "none"
      anim.cancel()
      this.portraitTarget.style.perspective = "none"
    }).catch(() => {})

    this.landTimer = setTimeout(() => this.openCoin(), START_DELAY_MS + COIN_FLIGHT_MS * COIN_LAND_AT)
  }

  openCoin() {
    this.portraitTarget.classList.add("is-landed")
    this.element.querySelector(".about-hero")?.classList.add("is-in")
    this.coinTarget.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.04)", offset: 0.2 }, { transform: "scale(0)" }],
      { duration: 560, easing: "cubic-bezier(.6,0,.4,1)", fill: "forwards" }
    )
    this.photoTarget.animate(
      [{ transform: "scale(1.22) rotate(-3deg)" }, { transform: "scale(.97) rotate(.5deg)", offset: 0.6 }, { transform: "none" }],
      { duration: 1100, easing: "cubic-bezier(.22,1,.36,1)", fill: "forwards" }
    )
  }

  // ── Sections, arrows, mascots, numbers ────────────────────────────────
  observeSections() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        this.observer.unobserve(entry.target)
        this.enter(entry.target)
      })
    }, { threshold: 0.35 })
    this.sectionTargets.forEach((el) => this.observer.observe(el))
    this.cueTargets.forEach((el) => this.observer.observe(el))
  }

  enter(el) {
    el.classList.add("is-in")
    const mascot = this.mascotTargets.find((m) => el.contains(m))
    if (mascot) this.popMascot(mascot)
    const number = this.numberTargets.find((n) => el.contains(n))
    if (number && el.dataset.count) this.countUp(number, Number(el.dataset.count))
  }

  popMascot(mascot) {
    mascot.animate([
      { opacity: 0, transform: "translateY(40px) scale(.6, .5)" },
      { opacity: 1, transform: "translateY(46px) scale(1.15, .6)", offset: 0.2 },
      { opacity: 1, transform: "translateY(-24px) scale(.85, 1.22)", offset: 0.5 },
      { opacity: 1, transform: "translateY(0) scale(1.2, .82)", offset: 0.72 },
      { opacity: 1, transform: "translateY(-6px) scale(.96, 1.05)", offset: 0.86 },
      { opacity: 1, transform: "none" }
    ], { duration: 900, delay: 350, easing: "cubic-bezier(.3,.7,.4,1)", fill: "backwards" })
      .finished.then(() => mascot.classList.add("is-idle")).catch(() => {})
  }

  countUp(el, to) {
    const start = performance.now()
    const duration = 1300
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration)
      el.textContent = String(Math.round(to * (1 - Math.pow(1 - p, 3))))
      if (p < 1) {
        requestAnimationFrame(step)
        return
      }
      el.animate(
        [{ transform: "none" }, { transform: "scale(1.08, .9)" }, { transform: "scale(.98, 1.03)" }, { transform: "none" }],
        { duration: 420, easing: "ease-out" }
      )
    }
    requestAnimationFrame(step)
  }

  // ── Mission quote ─────────────────────────────────────────────────────
  // Wraps every word in a span, keeping <em> (the highlighted word) intact.
  // Built with DOM APIs from the existing text, so no markup is injected.
  splitQuote() {
    if (!this.hasQuoteTarget || this.quoteTarget.dataset.split) return
    const wrap = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment()
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part))
            } else {
              const span = document.createElement("span")
              span.className = "about-w"
              span.textContent = part
              frag.appendChild(span)
            }
          })
          child.replaceWith(frag)
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          wrap(child)
        }
      })
    }
    wrap(this.quoteTarget)
    this.quoteTarget.dataset.split = "true"
    this.words = Array.from(this.quoteTarget.querySelectorAll(".about-w"))
  }

  lightQuote() {
    if (!this.hasQuoteTarget) return
    const top = this.quoteTarget.getBoundingClientRect().top
    const progress = (window.innerHeight * 0.85 - top) / (window.innerHeight * 0.5)
    this.lightWords(Math.min(1, Math.max(0, progress)))
  }

  lightWords(progress) {
    if (!this.words) return
    const lit = Math.round(progress * this.words.length)
    this.words.forEach((w, i) => w.classList.toggle("is-lit", i < lit))
  }
}
